// Deleting a template, from its page's danger zone or its row on the list
// (consistency-pass spec §4): one confirmation, one rule. The server refuses
// a template surveys use (Validation.ConstraintViolation) — the net behind
// the disabled actions.

import { toast } from "@pej/kore/components";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, isConstraintViolation } from "@/api/error";
import { templateKeys } from "@/api/keys";
import { useDeleteTemplate } from "@/api/templates";
import type { Template } from "@/api/types";
import { toastMutationError } from "@/components/error-toast";
import { IrreversibleConfirmDialog } from "@/components/section-page/irreversible-confirm";
import { countOf } from "@/lib/plural";

/** Why the template can't be deleted yet, or undefined when it can. */
export function deleteRefusal(surveyCount: number): string | undefined {
    if (surveyCount === 0) return undefined;
    return `Used by ${countOf(surveyCount, "survey")}. Switch ${surveyCount === 1 ? "it" : "them"} to another template first.`;
}

export interface DeleteTemplateDialogProps {
    template: Template;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onDeleted: () => void;
}

export function DeleteTemplateDialog({ template, open, onOpenChange, onDeleted }: DeleteTemplateDialogProps) {
    const deleteTemplate = useDeleteTemplate(template.workspaceId);
    const queryClient = useQueryClient();
    return (
        <IrreversibleConfirmDialog
            open={open}
            onOpenChange={onOpenChange}
            verb="Delete"
            entityName="template"
            matchValue={template.name}
            matchLabel="template name"
            consequences="No survey uses it."
            isPending={deleteTemplate.isPending}
            onConfirm={async () => {
                try {
                    await deleteTemplate.mutateAsync(template.id);
                    toast.success("Template deleted");
                    onDeleted();
                } catch (err) {
                    onOpenChange(false);
                    if (err instanceof ApiError) {
                        if (!isConstraintViolation(err)) {
                            toastMutationError("Delete failed", err);
                            return;
                        }
                        // A survey took the template since load. Say it as the
                        // page and the list do, never in the server's words:
                        // the refusal's own count, else the workspace's list
                        // the mutation has refetched by now.
                        const fresh = queryClient
                            .getQueryData<Template[]>(templateKeys.list(template.workspaceId))
                            ?.find((t) => t.id === template.id);
                        const surveyCount = err.countParam("surveyCount") ?? fresh?.surveyCount ?? 0;
                        toast.error(deleteRefusal(surveyCount) ?? "Failed to delete the template");
                        return;
                    }
                    throw err;
                }
            }}
        />
    );
}
